import type { FC } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import {
    shortMonthAndYear,
    shortMonthDayAndYearWithDayOfWeek,
    shortMonthAndDay,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip, IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { BasicDurations } from "app/services/DurationService";
import { PeriodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";

import { ChartTooltipFootnote } from "../../components/ChartTooltipFootnote";
import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../components/ChartTooltipWrapper";
import { formatNumber, formatPercent } from "../../utils/numberFormatters";
import CustomBullet, { CustomBulletContainer } from "../search-trends-page/common/CustomBullet";

import type { IChartTooltipDataItem, IChartTooltipDataItemRow } from "./types";

const formatDate = (date: string, format = shortMonthAndYear) => {
    return dayjs(date).format(format);
};

const TitleContainer = styled.div`
    font-family: Roboto, sans-serif;
    font-weight: 500;
    line-height: 20px;
    color: ${colorsPalettes.carbon["500"]};
    margin-bottom: 4px;
    font-size: 1rem;
`;

const IconButtonStyled = styled(IconButton)`
    padding: 10px;
    width: auto !important;
    border-radius: 18px;
    background: radial-gradient(
        167.71% 94.57% at 96.11% 3.99%,
        rgba(2, 199, 158, 0.3) 0%,
        rgba(3, 194, 162, 0.3) 17%,
        rgba(6, 180, 175, 0.3) 37%,
        rgba(11, 156, 196, 0.3) 59%,
        rgba(18, 123, 225, 0.3) 81%,
        rgba(25, 90, 254, 0.3) 100%
    );

    .inner-label {
        color: ${colorsPalettes.carbon["500"]};
    }
    ${SWReactIcons} {
        svg {
            path {
                fill: ${colorsPalettes.carbon["500"]} !important;
            }
        }
    }
`;

const getColumnsDefinition = ({
    periodOverPeriodType,
    isCluster,
    duration,
}: {
    periodOverPeriodType?: IPeriodOverPeriodCompareType | null;
    isCluster?: boolean;
    duration?: string;
}) => {
    let indexColumnTitle = translate("common.keyword");

    if (isCluster && periodOverPeriodType == null) {
        indexColumnTitle = translate("common.cluster");
    } else if (periodOverPeriodType != null) {
        indexColumnTitle = translate("demand_analysis.common.period");
    }

    const columnDefinition: ChartTooltipColumnDefinition<IChartTooltipDataItemRow>[] = [
        {
            key: "label",
            title: indexColumnTitle,
            align: "left",
            borderRight: false,
            selector: "label",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ label }) => {
                const formattedDate = formatDate(label.date);
                const labelText =
                    periodOverPeriodType != null
                        ? label.isMonthToDate
                            ? `${formattedDate}*`
                            : formattedDate
                        : label.name;

                if (label.isPreviousPeriod && label.color) {
                    return (
                        <div data-automation="chart-tooltip-label">
                            <Flex gap="4px">
                                <CustomBulletContainer>
                                    <CustomBullet color={String(label.color)} />
                                </CustomBulletContainer>
                                <ChartTooltip.LabelCell label={labelText} />
                            </Flex>
                        </div>
                    );
                }

                return (
                    <div data-automation="chart-tooltip-label">
                        <ChartTooltip.LabelCell
                            label={labelText}
                            backgroundColor={label.color}
                        />
                    </div>
                );
            },
        },
        {
            key: "volume",
            title: translate("common.volume"),
            align: "right",
            borderRight: duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            selector: "volume",
            size: ChartTooltip.ValueCellDefaultwidth,
            render: ({ volume }) => (
                <div data-automation="chart-tooltip-volume">
                    <ChartTooltip.ValueCell value={formatNumber(volume)} />
                </div>
            ),
        },
        {
            key: "change",
            get title() {
                return periodOverPeriodType == null
                    ? translate("global.change.mom")
                    : periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.PERIOD
                      ? translate("common.pop.change")
                      : translate("common.yoy.change");
            },
            align: "right",
            selector: periodOverPeriodType == null ? "monthlyChange" : "periodChange",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({
                monthlyChange,
                periodChange,
            }: {
                monthlyChange?: { value: number };
                periodChange?: { value: number };
            }) => {
                const change = monthlyChange ?? periodChange;
                if (!change) {
                    return <div data-automation="chart-tooltip-change" />;
                }
                return (
                    <div data-automation="chart-tooltip-change">
                        <ChartTooltip.ChangeCell
                            value={formatPercent(Math.abs(change.value))}
                            isDecrease={change.value < 0} // TODO use change.sentiment when the component supports "Stagnating"
                        />
                    </div>
                );
            },
        },
    ];

    const filteredColumnDefinition =
        duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS
            ? columnDefinition.filter((col) => col && col.key !== "change")
            : columnDefinition;

    if (isCluster && periodOverPeriodType == null) {
        return filteredColumnDefinition.toSpliced(1, 0, {
            key: "childrenCount",
            title: translate("common.keywords"),
            align: "right",
            borderRight: true,
            selector: "childrenCount",
            size: ChartTooltip.ValueCellDefaultwidth,
            render: ({ childrenCount }) => (
                <div data-automation="chart-tooltip-keywords-count">
                    <ChartTooltip.ValueCell value={childrenCount ? String(childrenCount) : ""} />
                </div>
            ),
        });
    }

    return filteredColumnDefinition;
};

interface ISearchVolumeOverTimeChartTooltipProps {
    tooltipDataItem: IChartTooltipDataItem;
    periodOverPeriodType: IPeriodOverPeriodCompareType | null;
    isDaily: boolean;
    isCluster?: boolean;
    untilDate?: string;
    anomalyDate?: string;
    duration?: string;
}

export const SearchVolumeOverTimeChartTooltip: FC<ISearchVolumeOverTimeChartTooltipProps> = ({
    tooltipDataItem,
    periodOverPeriodType,
    isDaily,
    isCluster,
    untilDate,
    anomalyDate,
    duration,
}) => {
    const columnDefinition = getColumnsDefinition({ periodOverPeriodType, isCluster, duration });

    let footnote: string | undefined;
    if (tooltipDataItem.isMonthToDate) {
        if (periodOverPeriodType != null && untilDate) {
            const onDate = formatDate(tooltipDataItem.date, shortMonthAndYear);
            const formattedUntilDate = formatDate(untilDate, shortMonthAndDay);
            footnote = translate("demand_analysis.search_volume_over_time.tooltip.footnote_partial_data.on_date", {
                onDate,
                untilDate: formattedUntilDate,
            });
        } else {
            footnote = translate("demand_analysis.search_volume_over_time.tooltip.footnote_partial_data.simple");
        }
    }

    let title =
        periodOverPeriodType != null
            ? tooltipDataItem.name
            : formatDate(tooltipDataItem.date, isDaily ? shortMonthDayAndYearWithDayOfWeek[0] : shortMonthAndYear);

    if (periodOverPeriodType == null && tooltipDataItem.isMonthToDate && untilDate) {
        const formattedUntilDate = formatDate(untilDate, shortMonthAndDay);
        title = translate("demand_analysis.search_volume_over_time.tooltip.title_until_date", {
            title,
            untilDate: formattedUntilDate,
        });
    }

    const enhancedTitle = anomalyDate ? (
        <Flex
            justifyContent="space-between"
            alignItems="center"
        >
            <TitleContainer>{title}</TitleContainer>
            <IconButtonStyled
                iconColor={colorsPalettes.carbon["500"]}
                type="flat"
                iconSize="sm"
                iconName="spark-ai"
                className="anomaly-button"
                data-anomaly-date={anomalyDate} // TODO: Find out why it doesn't work
                id={anomalyDate}
            >
                <span className="inner-label">{translate("anomaly.spike.chart.tooltip.button.label")}</span>
            </IconButtonStyled>
        </Flex>
    ) : (
        title
    );

    return (
        <Box
            padding="16px"
            borderRadius="8px"
            background={colorsPalettes.carbon[0]}
        >
            <ChartTooltipWrapper<IChartTooltipDataItemRow>
                width={450}
                title={enhancedTitle}
                data={tooltipDataItem.rows}
                columnDefinition={columnDefinition}
            >
                {footnote ? <ChartTooltipFootnote>{footnote}</ChartTooltipFootnote> : null}
            </ChartTooltipWrapper>
        </Box>
    );
};
